import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sun, Moon } from "lucide-react";
import { useTheme } from "../../lib/ThemeContext";

const links = [
    { label: "Home", target: "hero" },
    { label: "Work", target: "work" },
    { label: "Projects", target: "projects" },
    { label: "Contact", target: "contact" },
];

export const Nav = () => {
    const { theme, toggle } = useTheme();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            data-testid="nav-header"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className={`fixed top-0 inset-x-0 z-50 transition-[background,backdrop-filter,border-color] duration-300 ${
                scrolled
                    ? "bg-[var(--page)]/85 backdrop-blur-md border-b-2 border-[var(--ink)]"
                    : "bg-transparent border-b-2 border-transparent"
            }`}
        >
            <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-14 md:h-16 flex items-center justify-between">
                <a
                    data-testid="nav-logo"
                    href="#hero"
                    data-scroll-to="hero"
                    className="flex items-center gap-3 group"
                >
                    <span className="inline-flex items-center justify-center w-7 h-7 border-hard font-mono text-[11px] font-semibold bg-[var(--surface)] group-hover:bg-[var(--ink)] group-hover:text-[var(--page)] transition-colors">
                        MB
                    </span>
                    <span className="hidden sm:inline mono-label">
                        Manas Bhansali
                    </span>
                </a>

                <div className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <a
                            key={l.target}
                            href={`#${l.target}`}
                            data-scroll-to={l.target}
                            data-testid={`nav-link-${l.target}`}
                            className="mono-label px-3 py-2 link-underline hover:text-[var(--ink)]"
                        >
                            {l.label}
                        </a>
                    ))}
                    <button
                        data-testid="theme-toggle"
                        onClick={toggle}
                        aria-label="Toggle theme"
                        className="ml-3 inline-flex items-center gap-2 h-9 px-3 border-hard bg-[var(--surface)] hover:bg-[var(--ink)] hover:text-[var(--page)] transition-colors"
                    >
                        {theme === "light" ? (
                            <Moon className="w-3.5 h-3.5" strokeWidth={2.2} />
                        ) : (
                            <Sun className="w-3.5 h-3.5" strokeWidth={2.2} />
                        )}
                        <span className="mono-label !text-[10px]">
                            {theme === "light" ? "Dark" : "Light"}
                        </span>
                    </button>
                </div>

                <div className="md:hidden flex items-center gap-2">
                    <button
                        data-testid="theme-toggle-mobile"
                        onClick={toggle}
                        aria-label="Toggle theme"
                        className="inline-flex items-center justify-center w-9 h-9 border-hard bg-[var(--surface)]"
                    >
                        {theme === "light" ? (
                            <Moon className="w-4 h-4" strokeWidth={2} />
                        ) : (
                            <Sun className="w-4 h-4" strokeWidth={2} />
                        )}
                    </button>
                    <button
                        data-testid="nav-menu-btn"
                        onClick={() => setOpen((v) => !v)}
                        aria-label="Menu"
                        className="inline-flex items-center justify-center w-9 h-9 border-hard bg-[var(--surface)]"
                    >
                        <div className="flex flex-col gap-[3px]">
                            <span
                                className={`block h-[2px] w-4 bg-[var(--ink)] transition-transform ${open ? "translate-y-[5px] rotate-45" : ""}`}
                            />
                            <span
                                className={`block h-[2px] w-4 bg-[var(--ink)] transition-opacity ${open ? "opacity-0" : ""}`}
                            />
                            <span
                                className={`block h-[2px] w-4 bg-[var(--ink)] transition-transform ${open ? "-translate-y-[5px] -rotate-45" : ""}`}
                            />
                        </div>
                    </button>
                </div>
            </nav>

            {/* Mobile drawer */}
            <motion.div
                initial={false}
                animate={{ height: open ? "auto" : 0 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                className="md:hidden overflow-hidden border-t-2 border-[var(--ink)] bg-[var(--page)]"
            >
                <div className="px-5 py-4 flex flex-col">
                    {links.map((l) => (
                        <a
                            key={l.target}
                            href={`#${l.target}`}
                            data-scroll-to={l.target}
                            data-testid={`nav-link-mobile-${l.target}`}
                            onClick={() => setOpen(false)}
                            className="font-serif text-3xl py-3 border-b border-[var(--line-soft)] flex items-baseline justify-between"
                        >
                            <span>{l.label}</span>
                            <span className="mono-meta">
                                0{links.indexOf(l) + 1}
                            </span>
                        </a>
                    ))}
                </div>
            </motion.div>
        </motion.header>
    );
};
